import type { Scope } from '../../api/types/permissions';
import { Label } from '../ui/label';
import { SELECT_CLASS } from '../../lib/selectClass';

export interface RoleSelectGroup {
    key: string;
    label: string;
    roles: { roleId: string; roleName: string; scope?: Scope }[];
}

interface Props {
    /** Grupos de roles ya filtrados/agrupados (de `groupRolesByScope`). */
    groups: RoleSelectGroup[];
    /** roleId elegido (controlado). */
    value: string;
    /** Notifica el roleId elegido. */
    onChange: (roleId: string) => void;
    /** Prefijo de id para a11y (`${idPrefix}-role`). */
    idPrefix: string;
    /** Mensaje de error inline (opcional). */
    error?: string;
    /** Roles que se muestran deshabilitados, con `disabledNote` junto al nombre. */
    disabledRoleIds?: ReadonlySet<string>;
    disabledNote?: string;
}

/**
 * `<select>` de rol seccionado por nivel (`<optgroup>`). Presentacional puro — extraído de los forms de
 * alta (tenant + platform) para des-duplicar. Render por `roleName` (texto), `roleId` solo en
 * `value`/`key` (regla anti-UUID). Usa el `SELECT_CLASS` compartido.
 */
export function RoleSelect({
    groups,
    value,
    onChange,
    idPrefix,
    error,
    disabledRoleIds,
    disabledNote,
}: Props) {
    return (
        <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-role`}>Rol</Label>
            <select
                id={`${idPrefix}-role`}
                className={SELECT_CLASS}
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                }}
            >
                <option value="" disabled>
                    Elige un rol
                </option>
                {groups.map((g) => (
                    <optgroup key={g.key} label={g.label}>
                        {g.roles.map((r) => {
                            const disabled = disabledRoleIds?.has(r.roleId) ?? false;
                            return (
                                <option key={r.roleId} value={r.roleId} disabled={disabled}>
                                    {disabled && disabledNote
                                        ? `${r.roleName} (${disabledNote})`
                                        : r.roleName}
                                </option>
                            );
                        })}
                    </optgroup>
                ))}
            </select>
            {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
    );
}
